import React, { useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import ModuleFrame, { Chip, KV, useModuleTab } from '../../components/ModuleFrame';
import { AppFocusChips, useSelectedApp, withApp } from '../../components/AppSelector';
import { InstanceLinkSelect } from '../../components/InstanceLink';
import { createEntity, updateEntity, useEntityList, friendlyError } from '../../data/entities';
import { apiPost } from '../../utils/backendApi';
import { useAuth } from '../../AuthContext';
import AppsModule, { EnvironmentsPanel, StatusBadge } from './AppsModule';
import ComputeModule from './ComputeModule';
import { LibraryLifecycle } from '../../components/LifecycleRail';

// Deployments — Sandbox → UAT → Production for the selected app. The
// hosted instance (appInstance) + its UAT twin + the appDeploy ledger are
// the real environments; the appRelease record is the approval the deck
// shows (version, ref, knowledge version, gates, strategy). Release and
// rollback both go through POST /apps/:orgId/:appId/deploy, whose
// env-controls check enforces Administration's minimum roles.

const ENV_TONE = { live: 'ok', provisioning: 'info', suspended: 'warn', terminated: 'danger' };

export default function DeploymentsModule() {
  const { org, myRole } = useOutletContext();
  const { user } = useAuth();
  const isAdmin = ['admin', 'owner'].includes(myRole);
  const { app, loading: appsLoading } = useSelectedApp();
  const { docs: instances } = useEntityList('appInstance', { org: org.id }, { includeOrgWide: true });
  const { docs: releases } = useEntityList('appRelease', { org: org.id });
  const { docs: runs } = useEntityList('testRun', { org: org.id });
  const { docs: kdocs } = useEntityList('knowledgeDoc', { org: org.id });

  const prodInstances = (instances || []).filter((i) => i.envRole !== 'uat' && i.status !== 'terminated');
  const instance = (instances || []).find((i) => i.id === app?.links?.instanceAppId) || null;
  // Land on the org's instances until the selected app is wired to one.
  // The link control is on this tab (and on App Builder → Surfaces); the
  // pipeline is empty until an instance is chosen.
  const loaded = !appsLoading && instances !== null;
  const [tab, setTab] = useModuleTab(loaded && !instance ? 'instances' : 'pipeline');
  const twin = instance?.uatAppId ? (instances || []).find((i) => i.id === instance.uatAppId) || null : null;
  const appReleases = (releases || []).filter((r) => r.appId === app?.id);
  const latestRelease = appReleases[0] || null;
  const lastRun = (runs || []).filter((r) => r.appId === app?.id)[0] || null;
  const liveKnowledge = (kdocs || []).filter((d) => d.status === 'live' && (d.appIds || []).includes(app?.id));
  const knowledgeVersion = liveKnowledge.length ? `k${liveKnowledge.reduce((s, d) => s + (d.publishedVersion || 1), 0)}` : '—';

  const needRole = (env) => org.envControls?.[env] || (env === 'sandbox' ? 'member' : 'admin');
  const canRelease = (env) => (needRole(env) === 'member' ? myRole !== 'viewer' : isAdmin);

  const readiness = app ? [
    ['Instance linked', !!instance, instance ? instance.slug : 'pick one below'],
    ['UAT environment', !!twin, twin ? twin.slug : 'add the UAT add-on on the instance'],
    ['QA gates', lastRun?.status === 'passed', lastRun ? lastRun.status : 'no run'],
    ['Knowledge live', liveKnowledge.length > 0, knowledgeVersion],
    ['Release approved', ['approved', 'released'].includes(latestRelease?.status), latestRelease ? `${latestRelease.version} · ${latestRelease.status}` : 'none'],
  ] : [];

  return (
    <ModuleFrame
      title="DevOps"
      subtitle="Deployments · Sandbox → UAT → Production"
      accent="#3ecf8e"
      note={`DevOps, tab ${tab}${app ? `, app "${app.name}"` : ', no app focused'}${instance ? `, instance ${instance.slug} is ${instance.status}` : app ? ', no hosted instance yet' : ''}. NoVA: deploy_instance (the person confirms), instance_status, instance_costs, run_security_scan, open_dev_console.`}
      help="Each app runs on its own hosted instance (its own URL, database and layer repo). UAT is a full twin. A release is an approval record — version, ref, knowledge version, gates — that DevOps executes against the real environments and can roll back."
      context={(
        <>
          <AppFocusChips />
          {instance && <Chip tone={ENV_TONE[instance.status] || 'muted'}>{instance.slug} · {instance.status}</Chip>}
        </>
      )}
      lead={app ? <LibraryLifecycle org={org} app={app} moduleId="deployments" /> : null}
      tabs={[
        { id: 'pipeline', label: 'Environment pipeline' },
        { id: 'releases', label: 'Releases', count: appReleases.length },
        { id: 'readiness', label: 'Readiness' },
        { id: 'instances', label: 'Instances', count: prodInstances.length },
        { id: 'compute', label: 'Compute' },
      ]}
      tab={tab}
      onTab={setTab}
      verbs={[
        { label: 'New release', hint: 'approval record', onClick: () => setTab('releases'), primary: true, disabled: !app },
        { label: 'Deploy to UAT', hint: !app ? 'select an app' : !instance ? 'link an instance first' : !twin ? 'no UAT twin yet' : canRelease('uat') ? 'ready' : `needs org ${needRole('uat')}`, onClick: () => setTab('pipeline'), disabled: !twin || !canRelease('uat') },
        { label: 'Instances', hint: 'provision · UAT add-on · pushes', onClick: () => setTab('instances') },
      ]}
    >
      {tab === 'pipeline' && (
        <>
          {app && (
            <div className="mf-row">
              <span className="mf-muted">Hosted instance for <strong>{app.name}</strong></span>
              <InstanceLinkSelect orgId={org.id} app={app} instances={instances} canEdit={myRole !== 'viewer'} />
              {!prodInstances.length && <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" onClick={() => setTab('instances')}>Provision one</button>}
            </div>
          )}
          <ModuleFrame.Columns>
            <EnvCard label="Sandbox" tone="info" meta="this console + the sandbox corpus">
              <KV rows={[
                ['Who', `${needRole('sandbox')}+`],
                ['Knowledge', knowledgeVersion],
                ['Workflow', app?.links?.pipelineId ? 'linked' : 'none'],
                ['Preview', <Link to={withApp(`/orgs/${org.id}/builder`, app)}>App Builder preview →</Link>],
              ]} />
            </EnvCard>
            <EnvCard label="UAT" tone={twin ? ENV_TONE[twin.status] || 'muted' : 'muted'} meta={twin ? twin.slug : 'no UAT twin'}>
              {twin ? (
                <KV rows={[
                  ['Who', `${needRole('uat')}+`],
                  ['URL', <a href={twin.urls?.app} target="_blank" rel="noreferrer">{twin.urls?.app}</a>],
                  ['Status', <StatusBadge status={twin.status} />],
                  ['Health', <HealthChip monitor={twin.monitor} />],
                ]} />
              ) : <p className="cs-help">Add the UAT add-on on the instance (Instances tab → the app → Environments).</p>}
            </EnvCard>
            <EnvCard label="Production" tone={instance ? ENV_TONE[instance.status] || 'muted' : 'muted'} meta={instance ? instance.slug : 'no instance'}>
              {instance ? (
                <KV rows={[
                  ['Who', `${needRole('prod')}+`],
                  ['URL', <a href={instance.urls?.app} target="_blank" rel="noreferrer">{instance.urls?.app}</a>],
                  ['Status', <StatusBadge status={instance.status} />],
                  ['Health', <HealthChip monitor={instance.monitor} />],
                  ['Release', latestRelease?.status === 'released' ? latestRelease.version : '—'],
                ]} />
              ) : <p className="cs-help">Link or provision an instance.</p>}
            </EnvCard>
          </ModuleFrame.Columns>
          {instance && (
            <ModuleFrame.Panel title="Pushes" meta="layer repo → instance (remote-deploy on the instance's Dev Console)">
              <EnvironmentsPanel org={org} app={instance} uatDoc={twin} isAdmin={canRelease('uat')} />
              {!twin && <p className="cs-help">UAT pushes appear here once the UAT add-on is linked.</p>}
            </ModuleFrame.Panel>
          )}
        </>
      )}

      {tab === 'releases' && app && (
        <ModuleFrame.Columns>
          <NewRelease orgId={org.id} uid={user.uid} app={app} instance={instance} lastRun={lastRun} knowledgeVersion={knowledgeVersion} nextVersion={`v${appReleases.length + 1}.0`} />
          <ModuleFrame.Panel title="Release history" meta={`${appReleases.length} records`} className="mf-span-2">
            {!appReleases.length && <p className="cs-help">No releases yet.</p>}
            <div style={{ display: 'grid', gap: 8 }}>
              {appReleases.map((r) => <ReleaseCard key={r.id} orgId={org.id} rel={r} instance={instance} twin={twin} canRelease={canRelease} isAdmin={isAdmin} uid={user.uid} previous={appReleases.find((x) => x.status === 'released' && x.id !== r.id && (x.releasedAt || 0) < (r.releasedAt || Infinity))} />)}
            </div>
          </ModuleFrame.Panel>
        </ModuleFrame.Columns>
      )}
      {tab === 'releases' && !app && <p className="cs-help">Define an app in App Builder first.</p>}

      {tab === 'readiness' && (
        <ModuleFrame.Panel title={app ? `Readiness · ${app.name}` : 'Readiness'} meta="before Production">
          {!app && <p className="cs-help">Select an app.</p>}
          <ul className="mf-list">
            {readiness.map(([label, ok, hint]) => <li key={label} className="mf-row"><Chip tone={ok ? 'ok' : 'warn'}>{ok ? '✓' : '!'}</Chip><span className="mf-row-grow">{label}</span><span className="mf-muted">{hint}</span></li>)}
          </ul>
        </ModuleFrame.Panel>
      )}

      {tab === 'instances' && (
        <>
          {app && (
            <div className="mf-row" style={{ marginBottom: 12 }}>
              <span className="mf-muted">Hosted instance for <strong>{app.name}</strong></span>
              <InstanceLinkSelect orgId={org.id} app={app} instances={instances} canEdit={myRole !== 'viewer'} />
            </div>
          )}
          <AppsModule />
        </>
      )}
      {tab === 'compute' && <ComputeModule />}
    </ModuleFrame>
  );
}

export function HealthChip({ monitor }) {
  const state = monitor?.state || 'unknown';
  const tone = state === 'up' ? 'ok' : state === 'down' ? 'danger' : 'muted';
  return <Chip tone={tone} title={monitor?.lastCheckAt ? `checked ${new Date(monitor.lastCheckAt).toLocaleString()}` : ''}>{state}{monitor?.lastLatencyMs ? ` · ${monitor.lastLatencyMs} ms` : ''}</Chip>;
}

function EnvCard({ label, tone, meta, children }) {
  return (
    <ModuleFrame.Panel title={<span><Chip tone={tone}>{label}</Chip></span>} meta={meta}>
      {children}
    </ModuleFrame.Panel>
  );
}

function NewRelease({ orgId, uid, app, instance, lastRun, knowledgeVersion, nextVersion }) {
  const [version, setVersion] = useState(nextVersion);
  const [ref, setRef] = useState('main');
  const [notes, setNotes] = useState('');
  const [strategy, setStrategy] = useState({ rollout: 'all', audience: 'all', monitor: 'standard' });
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const gates = {
    qa: lastRun?.status === 'passed',
    business: false,
    guardrail: lastRun?.gates?.guardrails === 'pass',
    releaseAuth: false,
  };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      await createEntity('appRelease', { org: orgId }, {
        appId: app.id, instanceAppId: instance?.id || '', version: version.trim(), ref: ref.trim(), knowledgeVersion, notes: notes.trim(), gates, strategy, createdBy: uid,
      });
      setNotes('');
    } catch (ex) { setErr(friendlyError(ex)); } finally { setBusy(false); }
  };
  return (
    <ModuleFrame.Panel title="New release" meta="draft → approve → release">
      <form onSubmit={submit} style={{ display: 'grid', gap: 8 }}>
        <label className="mf-muted">Version<input className="cs-input" value={version} onChange={(e) => setVersion(e.target.value)} /></label>
        <label className="mf-muted">Layer ref (branch / tag)<input className="cs-input" value={ref} onChange={(e) => setRef(e.target.value)} /></label>
        <KV rows={[['Knowledge', knowledgeVersion], ['QA', lastRun ? lastRun.status : 'no run'], ['Instance', instance?.slug || 'not linked']]} />
        <label className="mf-muted">Notes<textarea className="mf-textarea" style={{ minHeight: 70 }} value={notes} onChange={(e) => setNotes(e.target.value)} /></label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
          <label className="mf-muted">Rollout<select className="cs-input" value={strategy.rollout} onChange={(e) => setStrategy({ ...strategy, rollout: e.target.value })}><option value="all">all at once</option><option value="staged" disabled>staged (wave 2)</option></select></label>
          <label className="mf-muted">Audience<select className="cs-input" value={strategy.audience} onChange={(e) => setStrategy({ ...strategy, audience: e.target.value })}><option value="all">everyone</option><option value="internal">internal first</option></select></label>
          <label className="mf-muted">Monitoring<select className="cs-input" value={strategy.monitor} onChange={(e) => setStrategy({ ...strategy, monitor: e.target.value })}><option value="standard">standard</option><option value="tight">tight (1 min probes)</option></select></label>
        </div>
        {err && <p className="cs-form-error" role="alert">{err}</p>}
        <div><button type="submit" className="cs-btn cs-btn-primary cs-btn-sm" disabled={busy || !version.trim()}>Create draft</button></div>
      </form>
    </ModuleFrame.Panel>
  );
}

function ReleaseCard({ orgId, rel, instance, twin, canRelease, isAdmin, uid, previous }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [word, setWord] = useState('');
  const patch = (p) => updateEntity('appRelease', { org: orgId }, rel.id, p);
  const approve = async () => {
    setBusy(true); setMsg('');
    try { await patch({ status: 'approved', approvedBy: uid, approvedAt: Date.now(), gates: { ...(rel.gates || {}), releaseAuth: true, business: true } }); } catch (e) { setMsg(friendlyError(e)); } finally { setBusy(false); }
  };
  const deploy = async (env, refOverride, after) => {
    if (!instance) { setMsg('Link an instance first.'); return; }
    setBusy(true); setMsg('');
    try {
      const out = await apiPost(`/apps/${orgId}/${instance.id}/deploy`, { env, ref: refOverride || rel.ref || '' });
      await patch(after(out));
      setMsg(`${env.toUpperCase()} deploy started (${out.targets.join(', ')}${out.ref ? ` @ ${out.ref}` : ''}).`);
      setWord('');
    } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };
  const tone = { draft: 'muted', approved: 'info', released: 'ok', rolled_back: 'warn', failed: 'danger' }[rel.status] || 'muted';
  const gates = rel.gates || {};
  return (
    <div className="mf-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <Chip tone={tone}>{rel.status}</Chip>
        <strong>{rel.version}</strong>
        <span className="mf-muted">ref {rel.ref || 'main'} · knowledge {rel.knowledgeVersion || '—'} · {new Date(rel.createdAt).toLocaleString()}</span>
        <span style={{ flex: 1 }} />
        {['qa', 'guardrail', 'business', 'releaseAuth'].map((g) => <Chip key={g} tone={gates[g] ? 'ok' : 'muted'}>{g}</Chip>)}
      </div>
      {rel.notes && <p className="cs-help" style={{ margin: 0 }}>{rel.notes}</p>}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }} data-agent-guard>
        {rel.status === 'draft' && isAdmin && <button type="button" className="cs-btn cs-btn-sm cs-btn-primary" disabled={busy || !gates.qa} onClick={approve} title={gates.qa ? 'Approve for release' : 'QA gate must pass'}>Approve</button>}
        {rel.status === 'draft' && !gates.qa && <span className="mf-muted">QA gate not passed — run the suite in QA & Testing</span>}
        {['approved', 'released'].includes(rel.status) && twin && canRelease('uat') && (
          <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" disabled={busy} onClick={() => deploy('uat', null, (out) => ({ deployId: out.deployId }))}>Deploy to UAT</button>
        )}
        {rel.status === 'approved' && canRelease('prod') && (
          <>
            <input className="cs-input" style={{ width: 120 }} value={word} onChange={(e) => setWord(e.target.value)} placeholder="type release" aria-label="confirm release" />
            <button type="button" className="cs-btn cs-btn-sm cs-btn-primary" disabled={busy || word !== 'release'} onClick={() => deploy('prod', null, (out) => ({ status: 'released', deployId: out.deployId, releasedAt: Date.now() }))}>Release to production</button>
          </>
        )}
        {rel.status === 'released' && canRelease('prod') && previous && (
          <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" disabled={busy} title={`Redeploy ${previous.version} (${previous.ref || 'main'})`} onClick={() => deploy('prod', previous.ref || 'main', (out) => ({ status: 'rolled_back', deployId: out.deployId }))}>Roll back to {previous.version}</button>
        )}
        {rel.status === 'released' && canRelease('prod') && !previous && <span className="mf-muted">no earlier release to roll back to</span>}
        {rel.status === 'draft' && <button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" disabled={busy} onClick={() => patch({ status: 'failed' }).catch((e) => setMsg(friendlyError(e)))}>Discard</button>}
        {rel.deployId && instance && <Link className="mf-muted" to={`/orgs/${orgId}/apps/${instance.id}`}>deploy {rel.deployId.slice(0, 6)} →</Link>}
      </div>
      {msg && <p className={msg.includes('started') ? 'mf-muted' : 'cs-form-error'} style={{ margin: 0 }}>{msg}</p>}
    </div>
  );
}
